<script setup lang="ts">
import { Link } from "@inertiajs/vue3"
import { ChevronRight, MoreHorizontal } from "lucide-vue-next"
import { computed } from "vue"

import AppTooltip from "@/components/AppTooltip.vue"
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"
import type { BreadcrumbItemType } from "@/types"

const props = withDefaults(defineProps<{
  breadcrumbs?: BreadcrumbItemType[]
}>(), {
  breadcrumbs: () => [],
})

// Collapse strategy: show first + last 2 when more than 4 items
const COLLAPSE_THRESHOLD = 4
const MAX_VISIBLE_ENDS = 1   // items shown at each end when collapsed

const collapsed = computed(() => props.breadcrumbs.length > COLLAPSE_THRESHOLD)

// Visible items as { item, index, isLast } — with a null sentinel for the ellipsis slot
type Slot =
  | { kind: "item"; item: BreadcrumbItemType; index: number; isLast: boolean }
  | { kind: "ellipsis"; hidden: BreadcrumbItemType[] }

const slots = computed((): Slot[] => {
  const crumbs = props.breadcrumbs
  if (!collapsed.value) {
    return crumbs.map((item, index) => ({
      kind: "item",
      item,
      index,
      isLast: index === crumbs.length - 1,
    }))
  }

  const head = crumbs.slice(0, MAX_VISIBLE_ENDS)
  const tail = crumbs.slice(crumbs.length - MAX_VISIBLE_ENDS)
  const hidden = crumbs.slice(MAX_VISIBLE_ENDS, crumbs.length - MAX_VISIBLE_ENDS)

  const result: Slot[] = []
  head.forEach((item, i) =>
    result.push({ kind: "item", item, index: i, isLast: false }),
  )
  result.push({ kind: "ellipsis", hidden })
  tail.forEach((item, i) =>
    result.push({
      kind: "item",
      item,
      index: crumbs.length - MAX_VISIBLE_ENDS + i,
      isLast: i === MAX_VISIBLE_ENDS - 1,
    }),
  )
  return result
})
</script>

<template>
  <Breadcrumb>
    <BreadcrumbList class="flex-nowrap">
      <template v-for="(slot, si) in slots" :key="si">

        <!-- Separator before every slot except the first -->
        <BreadcrumbSeparator v-if="si > 0">
          <ChevronRight class="size-3.5 shrink-0" />
        </BreadcrumbSeparator>

        <!-- Ellipsis with tooltip listing hidden items -->
        <BreadcrumbItem v-if="slot.kind === 'ellipsis'">
          <AppTooltip side="bottom" :delay-duration="0">
            <button
              type="button"
              class="text-muted-foreground hover:text-foreground hover:bg-accent flex size-6 items-center justify-center rounded transition-colors"
              aria-label="Show hidden pages"
            >
              <MoreHorizontal class="size-3.5" />
            </button>
            <template #content>
              <div class="flex flex-col gap-1 py-0.5 text-xs">
                <span
                  v-for="h in slot.hidden"
                  :key="h.href ?? h.title"
                  class="text-muted-foreground"
                >{{ h.title }}</span>
              </div>
            </template>
          </AppTooltip>
        </BreadcrumbItem>

        <!-- Regular item -->
        <BreadcrumbItem
          v-else-if="slot.kind === 'item'"
          class="min-w-0"
        >
          <!-- Last item — current page, not a link -->
          <BreadcrumbPage
            v-if="slot.isLast"
            class="text-foreground max-w-[200px] truncate font-medium"
          >
            {{ slot.item.title }}
          </BreadcrumbPage>

          <!-- Ancestor link -->
          <Link
            v-else
            :href="slot.item.href ?? '#'"
            class="text-muted-foreground hover:text-foreground hover:bg-accent max-w-[160px] truncate rounded px-1.5 py-0.5 text-sm transition-colors"
          >
            {{ slot.item.title }}
          </Link>
        </BreadcrumbItem>

      </template>
    </BreadcrumbList>
  </Breadcrumb>
</template>
